import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FaFacebookF, FaInstagram, FaTelegramPlane } from 'react-icons/fa';
import { FiCalendar, FiClock } from 'react-icons/fi';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchWithFallback, mockNews } from '../../utils/apiMocks';
import WithBackground from '../Common/WithBackground';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './style.css';

const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/backend/api', '/uploads');

const News = ({
  bg,
  overlay = 'dark',
  showOnlyFeatured = false,
  headerTitle,
  headerDescription,
  showViewAllButton = true,
  limit = 5
}) => {
  const { t, i18n } = useTranslation();

  // Generate correct news detail link based on current language
  const getNewsDetailLink = (newsId) => {
    const language = i18n.language || 'pl';

    const langPaths = {
      pl: '/aktualnosci',
      rus: '/rus/novosti',
      ua: '/ua/novyny',
      en: '/en/news'
    };

    return `${langPaths[language] || langPaths.pl}/${newsId}`;
  };

  // Generate correct news list link based on current language
  const getNewsListLink = () => {
    const language = i18n.language || 'pl';

    const langPaths = {
      pl: '/aktualnosci',
      rus: '/rus/novosti',
      ua: '/ua/novyny',
      en: '/en/news'
    };

    return langPaths[language] || langPaths.pl;
  };

  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load news articles
  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        const currentLang = i18n.language || 'pl';
        const apiUrl = process.env.REACT_APP_API_URL;
        let url = `${apiUrl}/news.php?lang=${currentLang}`;

        if (showOnlyFeatured) {
          url += '&featured=1';
        }

        const response = await fetchWithFallback(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          // Use mock data if available, otherwise use API data
          const newsItems = data.data || data.news || mockNews;
          let filteredNews = newsItems;

          if (showOnlyFeatured) {
            filteredNews = newsItems.filter(item => item.featured);
          }

          setNewsData(filteredNews);
        } else {
          console.error('API returned error:', data.error);
          setNewsData(mockNews);
        }
      } catch (error) {
        console.error('Error loading news:', error);
        setNewsData([]);
      } finally {
        setLoading(false);
      }
    };

    loadNews();
  }, [i18n.language, showOnlyFeatured]);

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const language = i18n.language || 'pl';

      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };

      return date.toLocaleDateString(language === 'pl' ? 'pl-PL' :
                                   language === 'rus' ? 'ru-RU' :
                                   language === 'ua' ? 'uk-UA' : 'en-US', options);
    } catch (error) {
      return dateString;
    }
  };

  const newsContent = (
    <section className='news'>

      <div className='news__container'>
        <div className='news__header'>
          <h2 className='news__title'>
            {headerTitle || t('news.title')}
          </h2>
          <p className='news__intro'>
            {headerDescription || t('news.subtitle')}
          </p>
        </div>

        <div className='news__carousel'>
          {loading ? (
            <div className='news__loading'>
              <p>{t('common.loading')}</p>
            </div>
          ) : newsData.length === 0 ? (
            <div className='news__empty'>
              <p>{t('news.noArticles')}</p>
            </div>
          ) : (
            <>
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={24}
                navigation={{
                  nextEl: '.news__next',
                  prevEl: '.news__prev',
                }}
                pagination={{
                  el: '.news__pagination',
                  clickable: true,
                }}
                breakpoints={{
                  0: {
                    slidesPerView: 1,
                    spaceBetween: 16,
                  },
                  769: {
                    slidesPerView: 2,
                    spaceBetween: 20,
                  },
                  1025: {
                    slidesPerView: 3,
                    spaceBetween: 24,
                  },
                }}
                className='news__swiper'
              >
                {newsData.map((article) => (
                  <SwiperSlide key={article.id}>
                    <article className='news-card'>
                      <div className='news-card__image'>
                        <img
                          src={`${UPLOADS_BASE_URL}/${article.image}`}
                          alt={article.title}
                          onError={(e) => {
                            console.log('Image failed to load:', article.image);
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                          onLoad={() => {
                            console.log('Image loaded successfully:', article.image);
                          }}
                        />
                        <div className='news-card__image-placeholder'>
                          <FiClock />
                        </div>
                        {article.is_featured && (
                          <div className='news-card__badge'>
                            {t('news.featured')}
                          </div>
                        )}
                      </div>

                      <div className='news-card__content'>
                        <div className='news-card__meta'>
                          <span className='news-card__date'>
                            <FiCalendar />
                            {formatDate(article.created_at)}
                          </span>
                        </div>

                        <h3 className='news-card__title'>{article.title}</h3>

                        {article.excerpt && (
                          <p className='news-card__excerpt'>{article.excerpt}</p>
                        )}

                        <Link
                          to={getNewsDetailLink(article.id)}
                          className='news-card__button btn'
                        >
                          {t('common.readMore')}
                        </Link>
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className='news__navigation'>
                <button className='news__prev'>
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M15 18L9 12L15 6'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>

                <button className='news__next'>
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M9 18L15 12L9 6'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>
              </div>

              <div className='news__pagination'></div>

          
            </>
          )}
        </div>

        <div className='news__social'>
          <h3 className='news__social-title'>{t('news.joinSocial')}</h3>
          <div className='news__social-links'>
            <a
              href='https://www.facebook.com/opusapt'
              className='header__social-link'
              aria-label='Facebook'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaFacebookF />
            </a>
            <a
              href='https://t.me/opusapt_agencjapracy'
              className='header__social-link'
              aria-label='Telegram'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaTelegramPlane />
            </a>
            <a
              href='https://www.instagram.com/opus.apt'
              className='header__social-link'
              aria-label='Instagram'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaInstagram />
            </a>
          </div>
        </div>
      </div>

<script src="https://elfsightcdn.com/platform.js" async></script>
<div className="elfsight-app-e7c47c8a-902e-463a-aecc-badf6eee6dd4" data-elfsight-app-lazy></div>
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {newsContent}
    </WithBackground>
  ) : (
    newsContent
  );
};

News.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
  showOnlyFeatured: PropTypes.bool,
  headerTitle: PropTypes.string,
  headerDescription: PropTypes.string,
  showViewAllButton: PropTypes.bool,
  limit: PropTypes.number,
};

News.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default News;